import { useState, useCallback } from "react"
import { router } from "@inertiajs/react"
import { ScheduleDialog } from "@/components/pieces/dialogs/ScheduleDialog"
import { MarkPublishedDialog } from "@/components/pieces/dialogs/MarkPublishedDialog"
import { ConfirmDeleteDialog } from "@/components/pieces/dialogs/ConfirmDeleteDialog"
import type { ActionKind, PieceForActionMenu, ChannelForActionMenu } from "@/lib/use-action-menu"

type DialogState =
  | {
      kind: "schedule"
      initialScheduledAt?: string | null
      initialManualPublish?: boolean
      initialUploadAt?: string | null
      initialUsePlatformScheduling?: boolean
    }
  | { kind: "mark_published"; variant: "expected" | "out_of_band"; initialUrl?: string | null }
  | { kind: "confirm_delete" }
  | null

/**
 * Centralized action dispatcher for a single piece. Returns:
 *
 *   * `fire(kind)` — invoke from an action menu item. Routes the kind
 *     to either an immediate POST or a dialog open.
 *   * `dialogs` — JSX element to render once per piece. Contains the
 *     currently-open dialog (if any), gated on local state.
 *
 * Consumers don't have to know which kinds open dialogs vs fire
 * immediately — they just call `fire(kind)`.
 */
export function useFireAction(
  piece: PieceForActionMenu & { name: string },
  channel: ChannelForActionMenu | undefined,
) {
  const [dialog, setDialog] = useState<DialogState>(null)
  const closeDialog = useCallback(() => setDialog(null), [])

  const fire = useCallback(
    (kind: ActionKind) => {
      switch (kind) {
        // -------- Dialogs --------
        case "schedule":
          setDialog({ kind: "schedule" })
          return
        case "reschedule":
          setDialog({
            kind: "schedule",
            initialScheduledAt: piece.slot?.publish_at ?? null,
            initialManualPublish: piece.slot?.manual_publish ?? false,
            initialUploadAt: piece.slot?.upload_at ?? null,
            initialUsePlatformScheduling: piece.slot?.use_platform_scheduling ?? false,
          })
          return
        case "mark_published":
          setDialog({ kind: "mark_published", variant: "expected" })
          return
        case "already_published":
          setDialog({
            kind: "mark_published",
            variant: "out_of_band",
            initialUrl: (piece as unknown as { published_url?: string | null }).published_url ?? null,
          })
          return
        case "delete":
          setDialog({ kind: "confirm_delete" })
          return

        // -------- Immediate POSTs --------
        case "queue_for_scheduling":
          post(`/pieces/${piece.id}/actions/queue_for_scheduling`)
          return
        case "queue_for_manual":
          post(`/pieces/${piece.id}/actions/queue_for_manual`)
          return
        case "unqueue":
          post(`/pieces/${piece.id}/actions/unqueue`)
          return
        case "backburner":
        case "unschedule_and_backburner":
        case "dont_schedule_and_backburner":
          post(`/pieces/${piece.id}/actions/to_backburner`)
          return
        case "queue_for_scheduling_from_backburner":
          post(`/pieces/${piece.id}/actions/bring_back_from_backburner`)
          return
        case "archive":
          post(`/pieces/${piece.id}/actions/archive`)
          return
        case "unarchive":
          post(`/pieces/${piece.id}/actions/unarchive`)
          return
        case "duplicate":
          // Server copies the piece into a fresh pending piece and redirects
          // to it; let Inertia navigate to the new piece (don't preserve
          // the published piece's scroll position).
          router.post(`/pieces/${piece.id}/actions/duplicate`)
          return
      }
    },
    [piece.id, piece.slot, piece.name, channel],
  )

  const dialogs = (
    <>
      {dialog?.kind === "schedule" && channel && (
        <ScheduleDialog
          open
          onClose={closeDialog}
          pieceId={piece.id}
          channelSlug={channel.slug}
          channelType={channel.channel_type}
          channelStatus={channel.credentials_status}
          channelSupportsPlatformScheduling={channel.supports_platform_scheduling ?? false}
          channelForcesPlatformScheduling={channel.forces_platform_scheduling ?? false}
          channelAllowsPlatformScheduling={channel.allow_platform_scheduling ?? false}
          channelDefaultPlatformScheduling={channel.default_to_platform_scheduling ?? false}
          channelLeadHours={channel.platform_scheduling_lead_hours ?? 0}
          pieceVisibility={(piece.fields?.visibility ?? null) as string | null}
          initialScheduledAt={dialog.initialScheduledAt}
          initialManualPublish={dialog.initialManualPublish}
          initialUploadAt={dialog.initialUploadAt}
          initialUsePlatformScheduling={dialog.initialUsePlatformScheduling}
        />
      )}
      {dialog?.kind === "mark_published" && (
        <MarkPublishedDialog
          open
          onClose={closeDialog}
          pieceId={piece.id}
          variant={dialog.variant}
          initialUrl={dialog.initialUrl}
        />
      )}
      {dialog?.kind === "confirm_delete" && (
        <ConfirmDeleteDialog
          open
          onClose={closeDialog}
          title={`Delete “${piece.name}”?`}
          description="This permanently removes the piece, its comments, attachments, slot, and todos. Cannot be undone."
          confirmLabel="Delete piece"
          deleteUrl={`/pieces/${piece.id}`}
        />
      )}
    </>
  )

  return { fire, dialogs }
}

function post(url: string) {
  router.post(url, {}, { preserveScroll: true })
}
